import { keyframes } from "@emotion/react";
import BackspaceIcon from "@mui/icons-material/Backspace";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Fade,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";

// Shake animation
const shake = keyframes`
  0% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-6px); }
  80% { transform: translateX(6px); }
  100% { transform: translateX(0); }
`;

const LockScreen = ({ mode = "unlock", onUnlock, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [shakeError, setShakeError] = useState(false);
  const [step, setStep] = useState(0);
  const [newPin, setNewPin] = useState("");

  const savedPin = localStorage.getItem("userPin") || "1234";

  // ✅ Trigger validation only when pin reaches 4
  useEffect(() => {
    if (pin.length === 4) {
      validatePin(pin);
    }
  }, [pin]);

  // ✅ Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (/^[0-9]$/.test(e.key) && pin.length < 4) {
        setPin((prev) => prev + e.key);
        setError("");
      } else if (e.key === "Backspace") {
        setPin((prev) => prev.slice(0, -1));
        setError("");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pin]);

  // ✅ Validate PIN
  const validatePin = (enteredPin) => {
    if (mode === "unlock") {
      if (enteredPin === savedPin) {
        onUnlock?.();
        setPin("");
      } else triggerError("Incorrect PIN. Try again.");
    }

    if (mode === "create") {
      if (step === 0) {
        setNewPin(enteredPin);
        setPin("");
        setStep(1);
      } else if (step === 1) {
        if (enteredPin === newPin) {
          localStorage.setItem("userPin", enteredPin);
          enqueueSnackbar("PIN created successfully", { variant: "success" });
          onClose?.();
        } else {
          triggerError("PINs do not match");
        }
      }
    }

    if (mode === "change") {
      if (step === 0) {
        if (enteredPin === savedPin) {
          setStep(1);
          setPin("");
        } else triggerError("Old PIN incorrect");
      } else if (step === 1) {
        setNewPin(enteredPin);
        setPin("");
        setStep(2);
      } else if (step === 2) {
        if (enteredPin === newPin) {
          localStorage.setItem("userPin", enteredPin);
          enqueueSnackbar("PIN changed successfully", { variant: "success" });
          onClose?.();
        } else {
          triggerError("PINs do not match");
        }
      }
    }
  };

  const triggerError = (msg) => {
    setError(msg);
    setShakeError(true);
    setTimeout(() => setShakeError(false), 600);
    setTimeout(() => setPin(""), 400);
  };

  // ✅ NumPad click
  const handleDigitClick = (digit) => {
    if (pin.length < 4) {
      setPin((prev) => prev + digit);
      setError("");
    }
  };

  const handleBackspace = () => {
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const getTitle = () => {
    if (mode === "unlock") return "Enter 4-digit PIN";
    if (mode === "create")
      return step === 0 ? "Create New PIN" : "Confirm New PIN";
    if (mode === "change") {
      if (step === 0) return "Enter Old PIN";
      if (step === 1) return "Enter New PIN";
      if (step === 2) return "Confirm New PIN";
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        p: 4,
        borderRadius: 3,
        textAlign: "center",
        width: 300,
        animation: shakeError ? `${shake} 0.4s` : "none",
      }}
    >
      <Box
        sx={{
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          gutterBottom
          sx={{ marginBottom: "20px" }}
        >
          {getTitle()}
        </Typography>
        {(mode === "change" || mode === "create") && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: "-25px",
              right: "-20px",
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* PIN dots */}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ marginBottom: "24px" }}
      >
        {[0, 1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              mx: 1,
              bgcolor:
                pin.length > i
                  ? error
                    ? "error.main"
                    : "primary.main"
                  : "grey.400",
              transition: "background-color 0.3s",
            }}
          />
        ))}
      </Box>

      {/* Error message */}
      <Fade in={!!error}>
        <Box sx={{ mb: 2, fontSize: "14px", color: "error.main" }}>{error}</Box>
      </Fade>

      {/* Number Pad */}
      <Grid container spacing={2}>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((item, idx) => (
          <Grid item xs={4} key={idx}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleDigitClick(item)}
              sx={{ height: 50, fontSize: 18, fontWeight: "bold" }}
            >
              {item}
            </Button>
          </Grid>
        ))}

        {/* Empty */}
        <Grid item xs={4}></Grid>

        {/* Zero */}
        <Grid item xs={4}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => handleDigitClick(0)}
            sx={{ height: 50, fontSize: 18, fontWeight: "bold" }}
          >
            0
          </Button>
        </Grid>

        {/* Backspace */}
        <Grid item xs={4}>
          <IconButton
            onClick={handleBackspace}
            sx={{
              height: 50,
              width: "100%",
              border: "1px solid rgba(0,0,0,0.2)",
              borderRadius: 1,
            }}
          >
            <BackspaceIcon fontSize="medium" />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default LockScreen;
